import "./styles.scss";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FileUploader } from "react-drag-drop-files";
import { motion, useMotionValue } from "framer-motion";
import ImageLightbox from "components/ImageLightbox";
import classnames from "classnames";

import Loader from "components/Loader";

import useResponsive from "libs/helpers/useResponsive";
import withProtocol from "libs/helpers/withProtocol";

import { ReactComponent as AttachmentIcon } from "assets/icons/new/attachment.svg";
import { ReactComponent as DocumentIcon } from "assets/icons/new/document.svg";
import { ReactComponent as DownloadIcon } from "assets/icons/new/download.svg";
import { ReactComponent as TrashIcon } from "assets/icons/new/trash.svg";

import {
  file_max_size,
  file_types,
  image_types,
} from "libs/file_upload_settings";

const mainClass = "files-upload";

const FilesUpload = ({
  layout,
  label,
  description,
  serverFiles,
  progress,
  inputId,
  isCompletelyDisabled,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const { min } = useResponsive();

  const [files, setFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);

  const objectUrls = useRef(new Map());
  const loadedImages = useRef(new Set());

  const previewX = useMotionValue(0);
  const previewY = useMotionValue(0);

  const isUploading = progress !== undefined && progress !== null && touched;
  const hasHoverPreview = !!hoverPreview;

  useEffect(() => {
    const urls = objectUrls.current;

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  useEffect(() => {
    setHoverPreview(null);

    const activeFiles = new Set(serverFiles || files);

    objectUrls.current.forEach((url, file) => {
      if (activeFiles.has(file)) return;

      URL.revokeObjectURL(url);
      objectUrls.current.delete(file);
      loadedImages.current.delete(url);
      setLightbox((previous) => (previous?.url === url ? null : previous));
    });
  }, [files, serverFiles, isUploading]);

  useEffect(() => {
    if (!hasHoverPreview) return;

    const hidePreview = () => setHoverPreview(null);

    window.addEventListener("scroll", hidePreview, true);
    window.addEventListener("resize", hidePreview);
    window.addEventListener("blur", hidePreview);

    return () => {
      window.removeEventListener("scroll", hidePreview, true);
      window.removeEventListener("resize", hidePreview);
      window.removeEventListener("blur", hidePreview);
    };
  }, [hasHoverPreview]);

  const handleError = () => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];

      onChange(newFiles);

      return newFiles;
    });
    setError(t("File already exists. Please choose another one."));
  };

  const handleFiles = (files) => {
    if (!files || files.length === 0) return;

    setError(null);

    setFiles((prevFiles) => {
      setTouched(true);

      const newFilesArray = Array.from(files);

      let foundFile = false;

      const uniqueFiles = newFilesArray.filter((newFile) => {
        foundFile = prevFiles.some(
          (existingFile) => existingFile.name === newFile.name,
        );

        return !foundFile;
      });

      const newFiles = [...prevFiles, ...uniqueFiles];

      if (foundFile) {
        handleError();
      } else if (typeof onChange === "function") {
        onChange(newFiles);
      }

      return newFiles;
    });
  };

  const handleServerFiles = (files) => {
    if (!files || files.length === 0) return;

    setError(null);
    setTouched(true);

    const filesArray = Array.from(files);

    const foundFile = serverFiles.some((serverFile) =>
      filesArray.some((file) => file.name === serverFile.name),
    );

    if (foundFile) {
      handleError();
    } else if (typeof onChange === "function") {
      onChange(filesArray);
    }
  };

  const getFilePreviewUrl = (file) => {
    if (file.location) return withProtocol(file.location);

    if (!objectUrls.current.has(file)) {
      objectUrls.current.set(file, URL.createObjectURL(file));
    }

    return objectUrls.current.get(file);
  };

  const handleFilePreview = (file) => {
    setHoverPreview(null);
    setLightbox({ url: getFilePreviewUrl(file), name: file.name });
  };

  const moveHoverPreview = ({ clientX, clientY }) => {
    const width = Math.min(200, window.innerWidth - 16);
    const height = Math.min(150, window.innerHeight - 16);
    const top = clientY - height - 16;

    previewX.set(
      Math.max(8, Math.min(clientX - width / 2, window.innerWidth - width - 8)),
    );
    previewY.set(
      top >= 8
        ? top
        : Math.max(8, Math.min(clientY + 16, window.innerHeight - height - 8)),
    );
  };

  const handleHoverPreview = (file, event) => {
    if (event.pointerType !== "mouse" || isUploading || lightbox || !min.md)
      return;

    const url = getFilePreviewUrl(file);
    moveHoverPreview(event);
    setHoverPreview({
      url,
      status: loadedImages.current.has(url) ? "loaded" : "loading",
    });
  };

  const handleHoverImageStatus = (url, status) => {
    if (status === "loaded") {
      loadedImages.current.add(url);
    } else {
      loadedImages.current.delete(url);
    }

    setHoverPreview((previous) =>
      previous?.url === url && previous.status !== status
        ? { ...previous, status }
        : previous,
    );
  };

  const handleFileRemove = (index) => {
    setFiles((prevFiles) => {
      const newFiles = prevFiles.filter((_, i) => i !== index);

      if (typeof onChange === "function") onChange(newFiles);

      return newFiles;
    });
  };

  const handleServerFileRemove = (file) => {
    setTouched(true);

    if (typeof onRemove === "function") onRemove(file);
  };

  if (
    !(serverFiles?.length > 0) &&
    !(files?.length > 0) &&
    isCompletelyDisabled
  )
    return null;

  return (
    <>
      <div
        className={classnames(mainClass, {
          [`${mainClass}--${layout}`]: !!layout,
          [`${mainClass}--error`]: !!error,
        })}
        onDragOver={isUploading ? (e) => e.preventDefault() : undefined}
        onDrop={isUploading ? (e) => e.preventDefault() : undefined}
      >
        <p>{label ?? t("Files")}</p>
        {!isCompletelyDisabled && (
          <FileUploader
            classes={`${mainClass}__input`}
            multiple={true}
            handleChange={!!serverFiles ? handleServerFiles : handleFiles}
            name={inputId ?? "file"}
            types={file_types}
            maxSize={file_max_size}
            hoverTitle=" "
            disabled={isUploading}
            onSizeError={() =>
              setError(
                t("The file is too large. Allowed maximum size is 20MB."),
              )
            }
          >
            <AttachmentIcon />
            <span>
              <span>
                {description ?? t("Click or drag a file to add an attachment")}
              </span>
              <small>
                {t("Acceptable format")}: doc, docx, pdf, jpg, png. <br />
                {t("Max size: 20MB.")}
              </small>
            </span>
            {isUploading && (
              <div className={`${mainClass}__progress`}>
                <div>
                  <p>{t("Uploading")}...</p>
                  <div className={`${mainClass}__progress-bar`}>
                    <div style={{ width: `${progress}%` }} />
                  </div>
                  <small>{progress}%</small>
                </div>
              </div>
            )}
          </FileUploader>
        )}
        {!!error && <small className={`${mainClass}__error`}>{error}</small>}
        {(serverFiles?.length > 0 || files?.length > 0) && (
          <div
            className={classnames(`${mainClass}__files`, {
              "is-disabled": isUploading,
            })}
            inert={isUploading}
          >
            {(serverFiles || files).map((file, index) => {
              const url = withProtocol(file?.location);
              const fileExtension = file?.name?.split(".").pop()?.toUpperCase();
              const isImage = image_types.includes(fileExtension);
              const isButton = isImage && (!!url || file instanceof Blob);
              const NameTag = isButton ? "button" : "span";

              return (
                <div
                  key={index}
                  className={`${mainClass}__file`}
                  onPointerEnter={
                    isButton
                      ? (event) => handleHoverPreview(file, event)
                      : undefined
                  }
                  onPointerMove={isButton ? moveHoverPreview : undefined}
                  onPointerLeave={
                    isButton ? () => setHoverPreview(null) : undefined
                  }
                >
                  <span>
                    <i>
                      <DocumentIcon />
                    </i>
                    <NameTag
                      type={isButton ? "button" : undefined}
                      onClick={
                        isButton ? () => handleFilePreview(file) : undefined
                      }
                    >
                      {file?.name}
                    </NameTag>
                  </span>
                  <div className={`${mainClass}__file-actions`}>
                    {!!url && (
                      <a
                        className="download"
                        href={url}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadIcon />
                      </a>
                    )}
                    {!isCompletelyDisabled && (
                      <button
                        className="delete"
                        type="button"
                        onClick={
                          !!serverFiles
                            ? () => handleServerFileRemove(file)
                            : () => handleFileRemove(index)
                        }
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {createPortal(
        min.md && !!hoverPreview && !lightbox && !isUploading && (
          <motion.div
            key={hoverPreview.url}
            className={`${mainClass}__image-preview`}
            style={{ x: previewX, y: previewY }}
            aria-hidden="true"
          >
            {hoverPreview.status === "loading" && <Loader color="blue" />}
            {hoverPreview.status === "error" && (
              <small>{t("Unable to load image.")}</small>
            )}
            <img
              src={hoverPreview.url}
              alt=""
              className={classnames({
                "is-loaded": hoverPreview.status === "loaded",
              })}
              ref={(image) => {
                if (
                  image?.complete &&
                  image.naturalWidth > 0 &&
                  hoverPreview.status === "loading"
                ) {
                  handleHoverImageStatus(hoverPreview.url, "loaded");
                }
              }}
              onLoad={() => handleHoverImageStatus(hoverPreview.url, "loaded")}
              onError={() => handleHoverImageStatus(hoverPreview.url, "error")}
            />
          </motion.div>
        ),
        document.body,
      )}

      {!!lightbox && (
        <ImageLightbox
          src={lightbox.url}
          onClose={() => setLightbox(null)}
          toolbarButtons={
            lightbox.url.startsWith("blob:")
              ? []
              : [
                  <a
                    key="download"
                    className={`${mainClass}__lightbox-download`}
                    href={lightbox.url}
                    download={lightbox.name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Download")}
                  </a>,
                ]
          }
        />
      )}
    </>
  );
};

export default FilesUpload;
